'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SetupConnectFormActions } from '@/components/setup/experience/SetupConnectFormShell';
import { SetupConnectStepSection } from '@/components/setup/experience/SetupConnectStepSection';
import { SetupSkipControl } from '@/components/setup/experience/SetupSkipControl';
import { SetupStoreColorField } from '@/components/setup/experience/SetupStoreColorField';
import { SetupStoreDigitalProductField } from '@/components/setup/experience/SetupStoreDigitalProductField';
import {
  SetupStoreCurrencyField,
  SetupStoreNameField,
} from '@/components/setup/experience/SetupStoreFormFields';
import { SetupStoreLogoField } from '@/components/setup/experience/SetupStoreLogoField';
import { SETUP_PATH_MOTION } from '@/components/setup/experience/constants';
import {
  setupManualStoreDefaults,
  setupManualStoreSchema,
  type SetupManualStoreFormData,
} from '@/components/setup/setupManualSchemas';
import type { SetupManualCreatedStore } from '@/components/setup/setupManualTypes';
import type { SetupSessionStore } from '@/components/setup/experience/types';
import { brandingConfig } from '@/config/branding';
import { useCreateStore, useUpdateStore } from '@/lib/api/stores';
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils';

interface SetupCreateStoreStepProps {
  onStoreCreated: (store: SetupManualCreatedStore) => void;
  /** Revisit mode (back navigation): prefill from the created store, save changes on continue. */
  existingStore?: SetupSessionStore | null;
  onStoreUpdated?: (store: SetupManualCreatedStore) => void;
}

/** Step 1 — create the store (identity + branding), common to every source path. */
export function SetupCreateStoreStep({
  onStoreCreated,
  existingStore = null,
  onStoreUpdated,
}: SetupCreateStoreStepProps) {
  const { t } = useTranslation('setup');
  const createStoreMutation = useCreateStore();
  const updateStoreMutation = useUpdateStore();
  const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null);

  const isRevisit = Boolean(existingStore);

  const methods = useForm<SetupManualStoreFormData>({
    resolver: zodResolver(setupManualStoreSchema),
    defaultValues: existingStore
      ? {
          name: existingStore.name,
          currency: existingStore.baseCurrency as SetupManualStoreFormData['currency'],
          primaryColor: existingStore.primaryColor,
          logoUrl: existingStore.logoUrl,
          digitalProduct: existingStore.digitalProduct,
        }
      : setupManualStoreDefaults,
    mode: 'onBlur',
  });
  const {
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const isSubmitting = createStoreMutation.isPending || updateStoreMutation.isPending;

  const onSubmit = async (data: SetupManualStoreFormData) => {
    try {
      setFormError(null);
      const logoUrl = data.logoUrl.trim();

      if (existingStore) {
        if (isDirty) {
          await updateStoreMutation.mutateAsync({
            storeId: existingStore.id,
            updatedData: {
              name: data.name.trim(),
              presentmentCurrencies: [data.currency],
              chargeCurrencies: [data.currency],
              baseCurrency: data.currency,
              type: 'shopify',
              credentials: null,
              multipleSubscriptionsPerCustomerEnabled: true,
              digitalProduct: data.digitalProduct,
              config: {
                primaryColor: data.primaryColor,
                logoUrl,
              },
            },
          });
        }

        onStoreUpdated?.({
          id: existingStore.id,
          name: data.name.trim(),
          baseCurrency: data.currency,
          primaryColor: data.primaryColor,
          logoUrl,
          digitalProduct: data.digitalProduct,
        });
        return;
      }

      const result = await createStoreMutation.mutateAsync({
        name: data.name.trim(),
        presentmentCurrencies: [data.currency],
        chargeCurrencies: [data.currency],
        baseCurrency: data.currency,
        type: 'shopify',
        credentials: null,
        multipleSubscriptionsPerCustomerEnabled: true,
        createDemoProducts: false,
        digitalProduct: data.digitalProduct,
        config: {
          primaryColor: data.primaryColor,
          logoUrl,
        },
      });

      onStoreCreated({
        id: result.id,
        name: data.name.trim(),
        baseCurrency: data.currency,
        primaryColor: data.primaryColor,
        logoUrl,
        digitalProduct: data.digitalProduct,
      });
    } catch (error) {
      setFormError(parseTRPCError(error));
    }
  };

  const { logo, logoDark, defaultIntegrationLogo } = brandingConfig;
  const brandLogoSrc = defaultIntegrationLogo ?? logo;
  const brandLogoDarkSrc = logoDark ?? brandLogoSrc;

  return (
    <div className="flex flex-col">
      <FormProvider {...methods}>
        <motion.form
          className="flex w-full max-w-none flex-col gap-0"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          initial={SETUP_PATH_MOTION.actions.initial}
          animate={SETUP_PATH_MOTION.actions.animate}
          transition={{ ...SETUP_PATH_MOTION.actions.transition, delay: 0.1 }}
        >
          <SetupConnectStepSection
            title={t('experience.connect.manualTitle')}
            description={t('experience.connect.manualSubtitle')}
            platform={{
              logoSrc: brandLogoSrc,
              logoDarkSrc: brandLogoDarkSrc,
              name: t('experience.sources.manual.name'),
              variant: 'brand',
            }}
            platformInTitle
            footer={
              <SetupConnectFormActions
                showBack={false}
                submitLabel={
                  isRevisit ? t('footer.continue') : t('steps.storeIntegration.manual.storeStep.submit')
                }
                isSubmitting={isSubmitting}
                leading={<SetupSkipControl />}
              />
            }
          >
            <div className="flex w-full flex-col divide-y divide-[color-mix(in_srgb,var(--border)_70%,transparent)] pb-4">
              <SetupStoreNameField />
              <SetupStoreCurrencyField />
              <SetupStoreLogoField />
              <SetupStoreColorField />
              <SetupStoreDigitalProductField />
            </div>

            {formError ? (
              <p
                className="text-destructive m-0 mb-4 rounded-md bg-[color-mix(in_srgb,var(--destructive)_8%,var(--background))] px-3 py-2.5 text-xs"
                role="alert"
              >
                {formError.message}
              </p>
            ) : null}
          </SetupConnectStepSection>
        </motion.form>
      </FormProvider>
    </div>
  );
}
